import { Box, Button, MenuItem, Select, Typography, styled, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedComponent,
  setSelectedTestCase,
  setSelectedProduct,
  resetAuthorings,
} from "../../Redux/TestAuthoring/TestAuthoring";

import AutocompleteComponent from "../../components-new/common/AutoCompleteComponent";
import { DeleteIcon } from "../../components-new/icons";
import { useEffect, useMemo } from "react";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: "300px",
    },
  },
};

const StyledSelect = styled(Select)`
  border-radius: 5px;
  border: 1px solid #d1dbff;
  font-size: 16px;
  color: #2b3539;
  min-width: 200px;
  max-height: 300px;
  background-color: #ffffff;
  .MuiSelect-select {
    padding: 13px 15px;
  }
`;

const StudioSelection = ({
  setRefreshFlag,
  products,
  modules,
  testCases,
  isLoadingTestcase,
  testCaseSearchKeyword,
  setTestCaseSearchKeyword,
  setHasUserClearedFilters,
}) => {
  const dispatch = useDispatch();

  const selectedProduct = useSelector((state) => state.authoringSlice.selectedProduct);
  const selectedModule = useSelector((state) => state.authoringSlice.selectedComponent);
  const selectedTestCase = useSelector((state) => state.authoringSlice.selectedTestCase);

  const handleProductChange = (event) => {
    setRefreshFlag(false);
    if (setHasUserClearedFilters) {
      setHasUserClearedFilters(false);
    }
    setTestCaseSearchKeyword("");
    Promise.resolve().then(() => {
      dispatch(setSelectedProduct(event.target.value));
    });
  };

  const handleOptionChange = (event, value, reason) => {
    setTestCaseSearchKeyword(value);
    if (reason === "input" && selectedTestCase) {
      dispatch(setSelectedTestCase(null));
    }
  };

  const handleTestCaseChange = (event, newValue, reason) => {
    if (setHasUserClearedFilters) {
      setHasUserClearedFilters(false);
    }

    // only update selection if user explicitly selected from dropdown
    // ignoore auto-complete or other automatic reasons
    if (reason === "selectOption" && newValue && typeof newValue === "object") {
      dispatch(setSelectedTestCase(newValue.idtest_case));
      setTestCaseSearchKeyword(newValue.Test_Case || "");
    } else if (reason === "clear" || (reason === "selectOption" && !newValue)) {
      dispatch(setSelectedTestCase(null));
      setTestCaseSearchKeyword("");
    }
  };

  // get test case count for selected module.
  const testCaseCount = useMemo(() => {
    if (!selectedModule || !modules?.length) return null;
    const moduleData = modules.find((m) => m.idmodules === selectedModule);
    return moduleData?.testCaseCount;
  }, [selectedModule, modules]);

  const handleClearFilter = () => {
    setRefreshFlag(false);
    dispatch(resetAuthorings());
    setTestCaseSearchKeyword("");
    if (setHasUserClearedFilters) {
      setHasUserClearedFilters(true);
    }
  };

  return (
    <>
      <Box>
        <Box>
          <Typography color="#8089A8" fontSize="13px" fontWeight={500}>
            Application
          </Typography>
        </Box>
        <StyledSelect
          MenuProps={MenuProps}
          value={selectedProduct ?? ""}
          onChange={handleProductChange}
          displayEmpty
        >
          <MenuItem disabled value="">
            Application
          </MenuItem>
          {products &&
            products.length > 0 &&
            (products ?? [{ label: "", value: "" }]).map((product) => (
              <MenuItem value={product.idproducts} key={product.idproducts}>
                {product.productname}
              </MenuItem>
            ))}
        </StyledSelect>
      </Box>
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography color="#8089A8" fontSize="13px" fontWeight={500}>
            Module
          </Typography>
          {testCaseCount !== null && testCaseCount !== undefined && (
            <Tooltip title={`Total test cases in this module: ${testCaseCount}`} arrow>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#2550d1",
                  backgroundColor: "#e3f2fd",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  cursor: "help",
                }}
              >
                {testCaseCount} TC
              </Typography>
            </Tooltip>
          )}
        </Box>
        <StyledSelect
          MenuProps={MenuProps}
          value={selectedModule}
          displayEmpty
          onChange={(event) => {
            setRefreshFlag(false);
            if (setHasUserClearedFilters) {
              setHasUserClearedFilters(false);
            }
            setTestCaseSearchKeyword("");
            dispatch(setSelectedComponent(event.target.value));
          }}
          disabled={!modules}
        >
          <MenuItem disabled value="">
            Module
          </MenuItem>
          {modules &&
            modules.length > 0 &&
            modules.map((module) => (
              <MenuItem key={module.idmodules} value={module.idmodules}>
                {module.modulename}
              </MenuItem>
            ))}
        </StyledSelect>
      </Box>
      <Box>
        <Box>
          <Typography color="#8089A8" fontSize="13px" fontWeight={500}>
            Test Scenario
          </Typography>
        </Box>
        <AutocompleteComponent
          key={`autocomplete-${selectedModule}`}
          disabled={!selectedModule}
          options={testCases?.length ? testCases : []}
          value={
            selectedTestCase && testCases?.length
              ? (() => {
                  const selectedTC = testCases.find(
                    (testCase) => testCase.idtest_case === selectedTestCase,
                  );
                  if (selectedTC && testCaseSearchKeyword === selectedTC.Test_Case) {
                    return selectedTC;
                  }
                  return null;
                })()
              : null
          }
          inputValue={testCaseSearchKeyword}
          onChange={handleTestCaseChange}
          isLoading={isLoadingTestcase}
          onInputChange={handleOptionChange}
          getOptionLabel={(testCase) => testCase.Test_Case ?? ""}
          placeholder="Search Test Cases"
        />
      </Box>
      <Button
        variant="outlined"
        startIcon={<DeleteIcon />}
        size="medium"
        onClick={handleClearFilter}
        sx={{
          color: "#828286",
          background: "#ffffff",
          height: "60px",
          fontSize: "16px",
          padding: "0 20px",
          border: "1px solid #E2E6F3",
          outline: 0,
          textTransform: "capitalize",
          mt: 2.5,
        }}
      >
        Clear filter
      </Button>
    </>
  );
};

export default StudioSelection;
